import { useCallback, useEffect, useState } from 'react';
import {
  Box, Stack, Group, Title, Text, Button, Paper, Table, Badge, Tabs,
  Alert, Loader, NumberInput, Checkbox, TextInput, SegmentedControl,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { adminApi, getAdminToken, setAdminToken, clearAdminToken } from '../../wheel-api/adminApi';
import { UI } from '../../theme/kioskTheme';
import './cvdlAdmin.css';
import './cvdlClaro.css';

const REFRESCO_MS = 30000;   // §8.6: "refresco cada 30 segundos"

export function CvdlAdmin() {
  const [autorizado, setAutorizado] = useState(Boolean(getAdminToken()));
  if (!autorizado) return <PinDireccion onOk={() => setAutorizado(true)} />;
  return <Panel onSalir={() => { clearAdminToken(); setAutorizado(false); }} />;
}

function PinDireccion({ onOk }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState(null);
  const [enviando, setEnviando] = useState(false);

  async function entrar(e) {
    e.preventDefault();
    setEnviando(true); setError(null);
    try {
      const r = await adminApi.armar(pin);
      setAdminToken(r.token);
      onOk();
    } catch (err) {
      setError(err.message || 'PIN incorrecto.');
    } finally { setEnviando(false); }
  }

  return (
    <Box className="cvdl-claro"
         style={{ minHeight: '100dvh', display: 'grid', placeItems: 'center' }}>
      <Paper withBorder radius="lg" p="xl" component="form" onSubmit={entrar} style={{ width: 340 }}>
        <Stack gap="md">
          <Title order={3} c="forvet.7">CVDL 2026</Title>
          <Text size="sm" c="forvet.6">PIN de dirección</Text>
          <input
            type="password" inputMode="numeric" value={pin} autoFocus
            onChange={e => { setPin(e.target.value.replace(/\D/g, '')); setError(null); }}
            style={{
              height: 52, fontSize: 24, textAlign: 'center', letterSpacing: 6,
              borderRadius: 10, border: `2px solid ${UI.borde}`, color: UI.tinta,
            }}
          />
          {error && <Alert color="red" radius="md">{error}</Alert>}
          <Button type="submit" loading={enviando} disabled={pin.length < 4}>ENTRAR</Button>
        </Stack>
      </Paper>
    </Box>
  );
}

function Panel({ onSalir }) {
  const [tab, setTab] = useState('ruleta');
  return (
    <Box className="cvdl-claro" p="md" style={{ minHeight: '100dvh' }}>
      <Group justify="space-between" mb="md" className="no-print">
        <Title order={2} c="forvet.7">CVDL 2026</Title>
        <Button variant="subtle" size="sm" onClick={onSalir}>Salir</Button>
      </Group>

      <Tabs value={tab} onChange={setTab} className="no-print" mb="md">
        <Tabs.List>
          <Tabs.Tab value="ruleta">Ruleta</Tabs.Tab>
          <Tabs.Tab value="ganadores">Ganadores</Tabs.Tab>
          <Tabs.Tab value="gemelos">Caravanas</Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {tab === 'ruleta' && <TableroRuleta />}
      {tab === 'ganadores' && <TableroGanadores />}
      {tab === 'gemelos' && <TableroGemelos />}
    </Box>
  );
}

function useTablero(cargar) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);

  const refrescar = useCallback(async () => {
    try { setDatos(await cargar()); setError(null); }
    catch (err) { setError(err.message); }
  }, [cargar]);

  useEffect(() => {
    refrescar();
    const t = setInterval(refrescar, REFRESCO_MS);
    return () => clearInterval(t);
  }, [refrescar]);

  return { datos, error, refrescar };
}

// ---- Ruleta -------------------------------------------------------------
function TableroRuleta() {
  const { datos, error, refrescar } = useTablero(adminApi.ruletaTablero);
  if (error) return <Alert color="red">{error}</Alert>;
  if (!datos) return <Loader />;

  const alertas = datos.premios.filter(p => p.alerta);

  return (
    <Stack gap="md">
      <Group gap="lg" wrap="wrap">
        <Kpi t="Fichas hoy" v={datos.fichas.hoy} />
        <Kpi t="Fichas total" v={`${datos.fichas.total} / ${datos.fichas.meta}`} />
        <Kpi t="Con email" v={`${datos.fichas.conEmail}%`} />
        <Kpi t="Giros hoy" v={datos.giros.hoy} />
        <Kpi t="Escaneos QR" v={datos.escaneosHoy} />
        <Kpi t="Franja" v={datos.franja || 'fuera'} />
      </Group>

      {datos.ultimoReseteo.dia !== datos.dia && (
        <Alert color='yellow'  radius="md">
          <p style={{color:"#000"}}>El reseteo diario no corrió hoy (último: {datos.ultimoReseteo.dia || 'nunca'}).
          Los cupos pueden estar agotados del día anterior.</p>
        </Alert>
      )}

      {alertas.length > 0 && (
        <Alert color="red" radius="md">
          <Text fw={700}>
            {alertas.length} premio(s) se apartan más de {datos.umbralDesvio} puntos de su
            probabilidad nominal
          </Text>
          <Text size="sm">
            §6.2: corregir esta noche, no el viernes — {alertas.map(a => a.premio).join(', ')}
          </Text>
        </Alert>
      )}

      <Paper withBorder radius="md" p="sm" style={{ overflowX: 'auto' }}>
        <Table striped highlightOnHover miw={560}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Premio</Table.Th><Table.Th>Marca</Table.Th>
              <Table.Th ta="right">Hoy / tope</Table.Th>
              <Table.Th ta="right">Franja</Table.Th>
              <Table.Th>Estado</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {datos.premios.map(p => (
              <Table.Tr key={p.slot}>
                <Table.Td fw={600}>{p.slot}. {p.premio}</Table.Td>
                <Table.Td>
                  <Badge  color={p.marca === 'IPG' ? 'var(--color-grand-accent)' : 'var(--color-primary)'}>{p.marca}</Badge>
                </Table.Td>
                <Table.Td ta="right">{p.hoy}{p.topeDia !== null ? ` / ${p.topeDia}` : ''}</Table.Td>
                <Table.Td ta="right">
                  {p.franjaActual ? `${p.franjaActual.restante}/${p.franjaActual.cap}` : '—'}
                </Table.Td>
                <Table.Td>
                  {!p.activo ? <Badge color="gray">inactivo</Badge>
                    : p.agotadoHoy ? <Badge color="#900707">agotado</Badge>
                      : <Badge color="#119007" variant="light">activo</Badge>}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      <Group className="no-print">
        <Button variant="outline" onClick={adminApi.exportarRuleta}>Exportar CSV</Button>
        <BotonReset onListo={refrescar} variante="outline" />
      </Group>
    </Stack>
  );
}

// ---- Ganadores ----------------------------------------------------------
// El tablero de arriba dice cuánto salió cada premio; esto dice quién se lo
// llevó. Es la pantalla que se abre cuando alguien vuelve al stand diciendo
// que perdió el código, y la que dirección mira al cierre de cada día.
function TableroGanadores() {
  const [alcance, setAlcance] = useState('todo');
  const [vista, setVista] = useState('premio');
  const [busca, setBusca] = useState('');

  const cargar = useCallback(() => adminApi.ruletaGiros(alcance), [alcance]);
  const { datos, error } = useTablero(cargar);

  if (error) return <Alert color="red">{error}</Alert>;
  if (!datos) return <Loader />;

  const q = busca.trim().toLowerCase();
  const coincide = (g) => !q || [g.nombre, g.premio, g.codigo, g.caravana]
    .some(v => String(v ?? '').toLowerCase().includes(q));

  const filtrados = datos.giros.filter(coincide);
  const grupos = datos.porPremio
    .map(p => ({ ...p, ganadores: p.ganadores.filter(coincide) }))
    .filter(p => p.ganadores.length);

  const hora = (f) => new Date(f).toLocaleString('es-AR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });

  const Fila = ({ g, conPremio }) => (
    <Table.Tr>
      <Table.Td w={120}><Text fz="var(--fs-small)" c="forvet.6">{hora(g.fecha)}</Text></Table.Td>
      {conPremio && (
        <Table.Td>
          <Text fw={600}>{g.slot}. {g.premio}</Text>
        </Table.Td>
      )}
      <Table.Td><Text fw={600}>{g.nombre}</Text></Table.Td>
      <Table.Td><Text ff="monospace" fz="var(--fs-small)">{g.telefono}</Text></Table.Td>
      <Table.Td w={90}>
        {g.caravana ? (
          <Text ff="monospace">{String(g.caravana).padStart(3, '0')}</Text>
        ) : <Text c="dimmed">—</Text>}
      </Table.Td>
      <Table.Td w={140}>
        {g.codigo
          ? <Badge variant="light" color={g.entregado ? '#119007' : 'gray'}>{g.codigo}</Badge>
          : <Text c="dimmed" fz="var(--fs-small)">sin código</Text>}
      </Table.Td>
    </Table.Tr>
  );

  const Encabezado = ({ conPremio }) => (
    <Table.Thead>
      <Table.Tr>
        <Table.Th>Cuándo</Table.Th>
        {conPremio && <Table.Th>Premio</Table.Th>}
        <Table.Th>Quién</Table.Th>
        <Table.Th>WhatsApp</Table.Th>
        <Table.Th>Caravana</Table.Th>
        <Table.Th>Código</Table.Th>
      </Table.Tr>
    </Table.Thead>
  );

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-end" wrap="wrap" className="no-print">
        <Group gap="lg" wrap="wrap">
          <Kpi t={alcance === 'hoy' ? 'Giros hoy' : 'Giros totales'} v={datos.total} />
          <Kpi t="Con código" v={datos.giros.filter(g => g.codigo).length} />
        </Group>
        <Group gap="sm" wrap="wrap">
          <SegmentedControl
            value={alcance} onChange={setAlcance} size="sm"
            data={[{ value: 'todo', label: 'Todo el evento' }, { value: 'hoy', label: 'Hoy' }]}
          />
          <SegmentedControl
            value={vista} onChange={setVista} size="sm"
            data={[{ value: 'premio', label: 'Por premio' }, { value: 'orden', label: 'Cronológico' }]}
          />
          <TextInput
            w={240} placeholder="Nombre, premio, código o caravana"
            value={busca} onChange={e => setBusca(e.currentTarget.value)}
          />
        </Group>
      </Group>

      {/* El teléfono se muestra recortado a propósito: esta tablet se ve desde
          el pasillo. El número completo sale en el CSV. */}
      <Text fz="var(--fs-small)" c="forvet.6" className="no-print">
        Los teléfonos van recortados porque la pantalla se ve desde el pasillo.
        El número entero está en el CSV.
        {datos.truncado && ` · Mostrando los ${datos.mostrados} giros más recientes de ${datos.total}.`}
      </Text>

      {!datos.total && <Alert color="gray">Todavía no giró nadie.</Alert>}

      {!!datos.total && !filtrados.length && (
        <Alert color="gray">Nada coincide con “{busca}”.</Alert>
      )}

      {vista === 'orden' && !!filtrados.length && (
        <Paper withBorder radius="md" p="sm" style={{ overflowX: 'auto' }}>
          <Table striped highlightOnHover miw={820}>
            <Encabezado conPremio />
            <Table.Tbody>
              {filtrados.map(g => <Fila key={g._id} g={g} conPremio />)}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {vista === 'premio' && grupos.map(p => (
        <Paper key={p.slot} withBorder radius="md" p="sm" style={{ overflowX: 'auto' }}>
          <Group justify="space-between" mb="xs" wrap="wrap">
            <Title order={4} c="forvet.8">{p.slot}. {p.premio}</Title>
            <Group gap="xs">
              <Badge color={p.marca === 'IPG' ? 'var(--color-grand-accent)' : 'var(--color-primary)'}>
                {p.marca}
              </Badge>
              <Badge variant="light">{p.ganadores.length} ganador(es)</Badge>
            </Group>
          </Group>
          <Table striped highlightOnHover miw={700}>
            <Encabezado />
            <Table.Tbody>
              {p.ganadores.map(g => <Fila key={g._id} g={g} />)}
            </Table.Tbody>
          </Table>
        </Paper>
      ))}

      <Group className="no-print">
        <Button variant="outline" onClick={adminApi.exportarRuleta}>Exportar CSV</Button>
      </Group>
    </Stack>
  );
}

// §7 pide poder disparar el reseteo a mano "por si el job falla justo un día
// del congreso". Es la operación más destructiva del sistema: fuera de la
// ventana de las 00:00–02:00 el backend devuelve 409 y hay que confirmar.
function BotonReset({ onListo, variante = 'filled' }) {
  const [enviando, setEnviando] = useState(false);

  async function resetear(forzar = false) {
    setEnviando(true);
    try {
      const r = await adminApi.resetearDia(forzar);
      notifications.show({ message: r.message || 'Día reseteado.', color: 'green' });
      onListo();
    } catch (err) {
      if (err.code === 'FUERA_DE_VENTANA') {
        if (window.confirm(
          (err.message || '') +
          '\n\nResetear ahora devuelve los cupos de hoy a cero: los premios con tope se ' +
          'podrían entregar de nuevo. ¿Confirmás?'
        )) return resetear(true);
      } else {
        notifications.show({ message: err.message, color: 'red' });
      }
    } finally { setEnviando(false); }
  }

  return (
    <Button size="sm" variant={variante} loading={enviando} onClick={() => resetear(false)}>
      Resetear el día
    </Button>
  );
}

// §6.5: "los pesos no se tocan durante un día en curso, sólo entre las 20:00
// y las 08:00". El aviso es del sistema; la decisión, de quien lo opera.

// ---- Caravanas ----------------------------------------------------------
function TableroGemelos() {
  const { datos, error, refrescar } = useTablero(adminApi.gemelosTablero);
  const [reporte, setReporte] = useState(null);
  const [listado, setListado] = useState(null);

  if (error) return <Alert color="red">{error}</Alert>;
  if (!datos) return <Loader />;

  if (reporte) return <ReporteEmparejado datos={reporte} onCerrar={() => setReporte(null)} />;
  if (listado) return <Entregadas datos={listado} onCerrar={() => setListado(null)} />;

  return (
    <Stack gap="md">
      <Paper withBorder radius="md" p="sm">
        <Group justify="space-between">
          <Text fw={700} size="lg" c="forvet.8">
            BOLSA ACTIVA: {datos.bolsa.activa || '—'}
          </Text>
          {!datos.bolsa.activa && <Text fz='var(--fs-small)' c="forvet.6">{datos.bolsa.motivo?.mensaje}</Text>}
        </Group>
      </Paper>

      <Group gap="lg" wrap="wrap">
        <Kpi t="Entregadas" v={`${datos.entregadas} / ${datos.numerosTotales * 2}`} />
        <Kpi t="Pares abiertos" v={datos.pares.abiertos} />
        <Kpi t="Pares cerrados" v={datos.pares.cerrados} />
        <Kpi t="Encontrados" v={`${datos.pares.encontrados} / ${datos.metas.paresEncontrados.meta}`} />
        <Kpi t="Fotos tomadas" v={datos.fotos.tomadas} />
        <Kpi t="Fotos impresas" v={datos.fotos.impresas} />
        <Kpi t="Sin consentimiento" v={datos.fotos.sinConsentimiento} />
        {/* Si sube y no baja: el job de avisos no corre, o la instancia
            de Evolution está desconectada. */}
        <Kpi t="Avisos pendientes" v={datos.avisosPendientes ?? 0} />
        <Kpi t="Huérfanos" v={`${datos.metas.huerfanos.valor}%`}
             alerta={datos.metas.huerfanos.valor > datos.metas.huerfanos.meta} />
      </Group>

      <LatidoAvisos datos={datos.avisosUltimaCorrida} />

      <Group className="no-print">
        <Button onClick={async () => setReporte(await adminApi.reporteEmparejado())}>
          REPORTE DE EMPAREJADO
        </Button>
        <Button variant="outline"
                onClick={async () => setListado(await adminApi.entregadas())}>
          QUIÉN TIENE CADA CARAVANA
        </Button>
        <Button variant="outline" onClick={adminApi.exportarGemelos}>Exportar CSV</Button>
      </Group>

      <LiberarPistas onListo={refrescar} />
    </Stack>
  );
}

// Un job que no corre y un job que corre sin nada para mandar se ven igual
// desde afuera: silencio. Esto los distingue.
function LatidoAvisos({ datos }) {
  if (!datos?.lastRunAt) {
    return (
      <Alert color="red" radius="md" className="no-print">
        <Text fw={700}>El job de avisos nunca corrió.</Text>
        <Text fz="var(--fs-small)">
          Lo más probable: el servidor no se reinició desde que se agregó. Sin él,
          nadie recibe las pistas por WhatsApp.
        </Text>
      </Alert>
    );
  }

  const minutos = Math.round((Date.now() - new Date(datos.lastRunAt)) / 60000);
  const viejo = minutos > 20;          // corre cada 15
  const caida = datos.razon === 'INSTANCIA_DESCONECTADA';
  // Una corrida que revienta se veía igual que una tranquila: el job late lo
  // mismo. Sin esto, un error de red repitiéndose cada 15 minutos es invisible.
  const error = datos.razon === 'ERROR';

  if (!viejo && !caida && !error) {
    return (
      <Text fz="var(--fs-small)" c="forvet.6" className="no-print">
        Avisos: última corrida hace {minutos} min · {datos.enviados || 0} enviados.
      </Text>
    );
  }

  return (
    <Alert color={caida ? 'orange' : 'red'} radius="md" className="no-print">
      <Text fw={700}>
        {caida ? 'La línea de WhatsApp está desconectada.'
          : error ? 'La última corrida de avisos falló.'
          : `El job de avisos no corre desde hace ${minutos} minutos.`}
      </Text>
      <Text fz="var(--fs-small)">
        {caida ? 'Los avisos están en pausa y salen solos cuando se reconecte el teléfono en Evolution.'
          : error ? 'Mirá el log del servidor: dice si fue la base o Evolution. Los avisos no se pierden, se reintentan en la próxima corrida.'
          : 'Debería correr cada 15 minutos. Revisá que el backend esté vivo.'}
      </Text>
    </Alert>
  );
}

// Quién tiene cada caravana. Agrupado por número: la pregunta que se hace el
// equipo no es "quién es el 001" sino "a quién le falta el gemelo".
function Entregadas({ datos, onCerrar }) {
  const [busca, setBusca] = useState('');

  const q = busca.trim().toLowerCase();
  const filas = !q ? datos.numeros : datos.numeros.filter(n =>
    String(n.numero).padStart(3, '0').includes(q) ||
    n.personas.some(p => (p.nombre || '').toLowerCase().includes(q)));

  const ESTADO = {
    OPEN: { t: 'Falta la otra mitad', c: 'orange' },
    CLOSED: { t: 'Las dos entregadas', c: 'blue' },
    MET: { t: 'Se encontraron', c: 'green' },
    ORPHAN: { t: 'Huérfano', c: 'red' },
  };

  const mitad = (n, letra) => {
    const p = n.personas.find(x => x.half === letra);
    if (!p) return <Text c="forvet.6" fz="var(--fs-small)">— sin entregar —</Text>;
    return (
      <Stack gap={0}>
        <Text fw={600}>{p.nombre}</Text>
        <Text fz="var(--fs-small)" c="forvet.6">
          {[p.telefono, p.ciudad].filter(Boolean).join(' · ') || '—'}
        </Text>
        <Text fz="var(--fs-small)" c="forvet.6">
          {p.foto ? 'con foto' : (p.consentImagen ? 'sin foto todavía' : 'no autorizó su imagen')}
          {p.dias?.length ? ` · ${p.dias.join(' ')}` : ''}
        </Text>
        {p.forzado && (
          <Badge color="orange" variant="light" size="sm" mt={2}>
            Forzada: {p.forzado.motivo || p.forzado.salteo.join(', ')}
          </Badge>
        )}
      </Stack>
    );
  };

  return (
    <Stack gap="md" className="reporte">
      <Group justify="space-between" className="no-print">
        <Button variant="subtle" onClick={onCerrar}>← Volver</Button>
        <Button onClick={() => window.print()}>IMPRIMIR</Button>
      </Group>

      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Stack gap={2}>
          <Title order={3} c="forvet.8">Quién tiene cada caravana</Title>
          <Text c="forvet.6">
            {datos.total} entregadas · {datos.numeros.length} números en juego
          </Text>
        </Stack>
        <TextInput
          className="no-print" w={260}
          placeholder="Buscar por número o nombre"
          value={busca} onChange={e => setBusca(e.currentTarget.value)}
        />
      </Group>

      {!filas.length && <Alert color="gray">Nada coincide con “{busca}”.</Alert>}

      {!!filas.length && (
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={70}>Nº</Table.Th>
              <Table.Th>Mitad A</Table.Th>
              <Table.Th>Mitad B</Table.Th>
              <Table.Th w={170}>Estado</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filas.map(n => (
              <Table.Tr key={n.numero}>
                <Table.Td>
                  <Text ff="monospace" fw={700} fz="lg">
                    {String(n.numero).padStart(3, '0')}
                  </Text>
                </Table.Td>
                <Table.Td>{mitad(n, 'A')}</Table.Td>
                <Table.Td>{mitad(n, 'B')}</Table.Td>
                <Table.Td>
                  <Badge color={ESTADO[n.estado]?.c || 'gray'} variant="light">
                    {ESTADO[n.estado]?.t || n.estado}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Stack>
  );
}

// §5.5: "en un evento siempre pasa algo: alguien que se va antes, un par que
// quiere jugar ya. Que lo pueda destrabar una persona del equipo."
//
// También es la única forma de probar el juego completo sin esperar: las pistas
// salen a las 3 horas del cierre y a las 10 del día siguiente, así que sin esto
// un ensayo de gemelos dura dos días.
function LiberarPistas({ onListo }) {
  const [numero, setNumero] = useState('');
  // Tildado por defecto: el silencio total es el resultado sorprendente,
  // y era el que estaba saliendo sin que nadie lo eligiera.
  const [avisar, setAvisar] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [reenviando, setReenviando] = useState(false);

  // Manda el mensaje consolidado ahora mismo, sin esperar la corrida del job.
  // Sirve para destrabar un par que quedó silenciado y para probar el envío.
  async function reenviar() {
    setReenviando(true);
    try {
      const r = await adminApi.reenviarAviso(Number(numero));
      notifications.show({
        message: `Caravana ${String(numero).padStart(3, '0')}: ${r.enviados} mensaje(s) enviado(s).`,
        color: 'green',
      });
    } catch (err) {
      notifications.show({ message: err.message || 'No se pudo reenviar.', color: 'red' });
    } finally { setReenviando(false); }
  }

  async function liberar() {
    setEnviando(true);
    try {
      const r = await adminApi.liberarPistas(Number(numero), avisar);
      notifications.show({
        message: `Caravana ${String(numero).padStart(3, '0')}: pistas liberadas` +
          (r.avisados ? `, ${r.avisados} aviso(s) enviado(s).` : ', sin avisar.'),
        color: 'green',
      });
      setNumero('');
      onListo?.();
    } catch (err) {
      notifications.show({ message: err.message || 'No se pudo liberar.', color: 'red' });
    } finally { setEnviando(false); }
  }

  return (
    <Paper withBorder radius="md" p="md" className="no-print">
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Stack gap={2}>
          <Text fw={600} c="forvet.8">Liberar las pistas de un par</Text>
          <Text fz="var(--fs-small)" c="forvet.6">
            Saltea la espera y muestra las tres pistas de una vez. No se puede deshacer.
          </Text>
          {/* Por defecto no avisa: quien libera a mano suele tener a las dos
              personas enfrente, y en un ensayo nunca se quiere mandar nada. */}
          <Checkbox
            size="sm" checked={avisar}
            onChange={e => setAvisar(e.currentTarget.checked)}
            label="Avisarles por WhatsApp (un solo mensaje)"
            description={avisar ? undefined
              : "Sin esto no se les avisa nunca por este par."}
          />
        </Stack>
        <Group gap="sm">
          <NumberInput
            value={numero} onChange={setNumero}
            min={1} max={200} w={120} placeholder="Nº"
            hideControls
          />
          <Button loading={enviando} disabled={!numero} onClick={liberar}>
            LIBERAR
          </Button>
          <Button variant="outline" loading={reenviando} disabled={!numero}
                  onClick={reenviar}>
            REENVIAR AVISO
          </Button>
        </Group>
      </Group>
    </Paper>
  );
}

// §2: la lista que el equipo usa en el receso para armar la bolsa B activa.
// §7 pide que se imprima en una hoja y sea legible.
function ReporteEmparejado({ datos, onCerrar }) {
  return (
    <Stack gap="md">
      <Group justify="space-between" className="no-print">
        <Button variant="subtle" onClick={onCerrar}>← Volver</Button>
        <Button onClick={() => window.print()}>IMPRIMIR</Button>
      </Group>

      <Paper withBorder radius="md" p="lg" className="reporte">
        <Title order={3} c="forvet.8">Reporte de emparejado · CVDL 2026</Title>
        <Text c="forvet.6" mb="xs" fz='var(--fs-small)'>
          {new Date(datos.generadoEl).toLocaleString('es-AR')} · {datos.total} números esperando su otra mitad
        </Text>
        <Text fz='var(--fs-small)'  mb="md">
          Sacá de la <b>bolsa B</b> exactamente estos números y armá la bolsa B activa.
        </Text>

        <Text fw={700} fz='var(--fs-small)' mb={4}>Falta la mitad B ({datos.faltaB.length})</Text>
        <div className="grilla">
          {datos.faltaB.map(n => (
            <span key={n} className="num">{String(n).padStart(3, '0')}</span>
          ))}
        </div>

        {datos.faltaA.length > 0 && (
          <>
            <Text fw={700} mt="md" mb={4}>Falta la mitad A ({datos.faltaA.length})</Text>
            <div className="grilla">
              {datos.faltaA.map(n => (
                <span key={n} className="num">{String(n).padStart(3, '0')}</span>
              ))}
            </div>
          </>
        )}
      </Paper>
    </Stack>
  );
}

function Kpi({ t, v, alerta }) {
  return (
    <Paper withBorder radius="md" p="sm" style={{ minWidth: 140 }}>
      <Text fz='var(--fs-small)' c="forvet.6" tt="uppercase">{t}</Text>
      <Text fw={800} style={{ fontSize: 26 }} c={alerta ? 'red' : 'forvet.8'}>{v}</Text>
    </Paper>
  );
}
